// ==========================================================
// LikesUI v2.0 — ❤️ Me Gusta universal + animaciones
// Se integra con cualquier tarjeta de negocio del sitio
// ==========================================================
"use strict";

(function (global) {

  const API_BASE = (function() {
    const loc = window.location;
    const isLocal = /localhost|127\.0\.0\.1/.test(loc.hostname);
    return isLocal ? `${loc.protocol}//${loc.hostname}:3000/api/v1` : `${loc.origin}/api/v1`;
  })();

  const LIKES_CACHE_KEY = '_pya_liked_ids';
  let likesCache = null; // Set<string> | null = not loaded
  let _animInjected = false; // CSS keyframes injected flag

  // ===== INYECTAR CSS DE ANIMACIONES (una vez) =====
  function injectAnimations() {
    if (_animInjected) return;
    _animInjected = true;
    try {
      const style = document.createElement('style');
      style.id = 'pya-likes-animations';
      style.textContent = `
        @keyframes pya-heart-beat {
          0%   { transform: scale(1); }
          15%  { transform: scale(1.4); }
          30%  { transform: scale(1); }
          45%  { transform: scale(1.2); }
          60%  { transform: scale(1); }
        }
        @keyframes pya-heart-unlike {
          0%   { transform: scale(1); }
          50%  { transform: scale(0.8); }
          100% { transform: scale(1); }
        }
        @keyframes pya-particle-fly {
          0%   { transform: translate(0,0) scale(1); opacity: 1; }
          100% { transform: translate(var(--dx), var(--dy)) scale(0); opacity: 0; }
        }
        @keyframes pya-ripple {
          0%   { transform: scale(0.5); opacity: 0.6; }
          100% { transform: scale(2.5); opacity: 0; }
        }
        @keyframes pya-glow-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.3); }
          50%      { box-shadow: 0 0 0 8px rgba(239,68,68,0); }
        }
        .pya-like-btn {
          position: relative;
          background: none !important;
          border: none !important;
          cursor: pointer;
          font-size: 1.2rem;
          padding: 0.4rem;
          border-radius: 50%;
          width: 36px;
          height: 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.15s ease;
          outline: none !important;
          -webkit-tap-highlight-color: transparent;
          z-index: 5;
        }
        .pya-like-btn:hover {
          transform: scale(1.1);
        }
        .pya-like-btn:active {
          transform: scale(0.95);
        }
        .pya-like-btn i {
          transition: color 0.2s ease, transform 0.2s ease;
          font-size: 1.2rem;
          line-height: 1;
        }
        .pya-like-btn .pya-particle {
          position: absolute;
          width: 6px;
          height: 6px;
          border-radius: 50%;
          pointer-events: none;
          z-index: 10;
        }
        .pya-like-btn .pya-ripple-el {
          position: absolute;
          top: 50%;
          left: 50%;
          width: 20px;
          height: 20px;
          margin: -10px 0 0 -10px;
          border-radius: 50%;
          border: 2px solid #ef4444;
          pointer-events: none;
          z-index: -1;
        }
        .pya-like-count-badge {
          position: absolute;
          top: -3px;
          right: -5px;
          min-width: 17px;
          height: 17px;
          padding: 0 4px;
          background: #ef4444;
          color: #fff;
          border-radius: 999px;
          font-size: 9px;
          font-weight: 700;
          line-height: 17px;
          text-align: center;
          box-shadow: 0 1px 4px rgba(0,0,0,0.25);
          pointer-events: none;
          z-index: 6;
        }
      `;
      document.head.appendChild(style);
    } catch (_) {}
  }

  // ===== CARGAR LIKED IDs =====
  async function loadLikedIds() {
    let token = getToken();
    if (!token) {
      // Intentar recuperar desde cookie HttpOnly
      token = await recoverTokenFromCookie();
      if (!token) {
        likesCache = new Set();
        return likesCache;
      }
    }
    try {
      const cached = sessionStorage.getItem(LIKES_CACHE_KEY);
      if (cached) {
        const arr = JSON.parse(cached);
        if (Array.isArray(arr)) {
          likesCache = new Set(arr);
          return likesCache;
        }
      }
    } catch {}

    try {
      const resp = await fetch(`${API_BASE}/likes/mine`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await resp.json();
      if (data.status === 'success' && data.data?.likes) {
        const ids = data.data.likes.map(l => l.business_id || l.business?.id).filter(Boolean);
        likesCache = new Set(ids);
        try { sessionStorage.setItem(LIKES_CACHE_KEY, JSON.stringify(Array.from(likesCache))); } catch {}
      } else {
        likesCache = new Set();
      }
    } catch {
      likesCache = new Set();
    }
    return likesCache;
  }

  // Token solo en memoria (renovado desde la cookie HttpOnly); ya no se lee de localStorage
  let memToken = null;
  function getToken() {
    return memToken;
  }

  /**
   * Verifica si existe la cookie de sesión (__Host-session_hint o session_hint legacy).
   * Esta cookie NO es HttpOnly, la setea el backend al hacer login.
   * Si no existe, el usuario no está logueado → no intentar refresh.
   */
  function hasSessionHint() {
    try {
      return document.cookie.split(';').some(c => {
        const t = c.trim();
        return t.startsWith('__Host-session_hint=') || t.startsWith('session_hint=');
      });
    } catch { return false; }
  }

  // ===== RECUPERAR TOKEN DESDE COOKIE HTTPONLY =====
  // Si localStorage no tiene token, intentamos usar el refresh_token
  // de la cookie HttpOnly para obtener uno nuevo.
  // Solo se intenta si existe la session_hint (usuario logueado).
  let _recoveringToken = false;
  async function recoverTokenFromCookie() {
    if (_recoveringToken) return null;
    // Short-circuit: si no hay session_hint, el usuario no está logueado
    if (!hasSessionHint()) return null;
    _recoveringToken = true;
    try {
      const resp = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!resp.ok) return null;
      const data = await resp.json();
      const token = data?.data?.tokens?.access_token || data?.access_token || null;
      if (token) {
        memToken = token; // solo en memoria mientras la página está abierta
        return token;
      }
      return null;
    } catch {
      return null;
    } finally {
      _recoveringToken = false;
    }
  }

  function isLiked(businessId) {
    return likesCache ? likesCache.has(businessId) : false;
  }

  // ===== BURBUJA DE PARTÍCULAS (animación espectacular) =====
  function burstParticles(btnEl) {
    const colors = ['#ef4444', '#f97316', '#f59e0b', '#ec4899', '#8b5cf6'];
    const rect = btnEl.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;

    for (let i = 0; i < 6; i++) {
      const particle = document.createElement('span');
      particle.className = 'pya-particle';
      const angle = (Math.PI * 2 * i) / 6 + (Math.random() - 0.5) * 0.5;
      const dist = 20 + Math.random() * 25;
      const dx = Math.cos(angle) * dist;
      const dy = Math.sin(angle) * dist;
      const size = 4 + Math.random() * 6;
      particle.style.cssText = `
        width:${size}px; height:${size}px;
        background:${colors[i % colors.length]};
        left:${cx}px; top:${cy}px;
        --dx:${dx}px; --dy:${dy}px;
        animation: pya-particle-fly 0.6s ease-out forwards;
        border-radius:${Math.random() > 0.5 ? '50%' : '2px'};
      `;
      btnEl.appendChild(particle);
      setTimeout(() => particle.remove(), 700);
    }
  }

  // ===== ONDA RIPPLE =====
  function addRipple(btnEl) {
    const ripple = document.createElement('span');
    ripple.className = 'pya-ripple-el';
    ripple.style.animation = 'pya-ripple 0.5s ease-out forwards';
    btnEl.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);
  }

  // ===== ACTUALIZAR ESTADO DEL BOTÓN (cuando carga la caché) =====
  function updateButtonState(btn, businessId) {
    const nowLiked = isLiked(businessId);
    const icon = btn.querySelector('i');
    if (!icon) return;
    const isCurrently = btn.dataset.liked === 'true';
    if (nowLiked === isCurrently) return; // ya está correcto
    icon.className = nowLiked ? 'fas fa-heart' : 'far fa-heart';
    icon.style.color = nowLiked ? '#ef4444' : '#9ca3af';
    icon.style.textShadow = nowLiked ? '0 0 6px rgba(239,68,68,0.4)' : 'none';
    btn.dataset.liked = nowLiked ? 'true' : 'false';
    btn.setAttribute('aria-label', nowLiked ? 'Quitar me gusta' : 'Dar me gusta');
    btn.title = nowLiked ? 'Quitar me gusta' : 'Me gusta';
  }

  // ===== CREAR BOTÓN CORAZÓN (con animaciones completas) =====
  // Opciones: { count: number } = contador inicial de likes
  function createLikeButton(businessId, options) {
    injectAnimations();
    options = options || {};

    // Estado inicial: gris (no liked) hasta que la caché confirme
    const liked = likesCache ? isLiked(businessId) : false;
    const btn = document.createElement('button');
    btn.className = 'pya-like-btn';
    btn.setAttribute('aria-label', liked ? 'Quitar me gusta' : 'Dar me gusta');
    btn.dataset.bizId = businessId;
    btn.dataset.liked = liked ? 'true' : 'false';
    btn.title = liked ? 'Quitar me gusta' : 'Me gusta';

    // Icono
    const icon = document.createElement('i');
    icon.className = liked ? 'fas fa-heart' : 'far fa-heart';
    icon.style.color = liked ? '#ef4444' : '#9ca3af';
    icon.style.textShadow = liked ? '0 0 6px rgba(239,68,68,0.4)' : 'none';
    btn.appendChild(icon);

    // Badge de contador (si se proporciona)
    if (typeof options.count === 'number') {
      btn.dataset.likesCount = String(options.count);
      const badge = document.createElement('span');
      badge.className = 'pya-like-count-badge';
      badge.textContent = options.count > 99 ? '99+' : String(options.count);
      btn.appendChild(badge);
    }

    // Si la caché no estaba lista, actualizar cuando cargue
    if (!likesCache) {
      const tryUpdate = () => {
        if (likesCache) {
          updateButtonState(btn, businessId);
        } else {
          setTimeout(tryUpdate, 100);
        }
      };
      loadLikedIds().then(() => {
        updateButtonState(btn, businessId);
      }).catch(() => {});
    }

    // Click handler
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      await handleClick(businessId, btn);
    });

    return btn;
  }

  // ===== ACTUALIZAR BADGE DE CONTADOR =====
  function updateLikesCountBadge(btnEl, count) {
    btnEl.dataset.likesCount = String(count);
    var badge = btnEl.querySelector('.pya-like-count-badge');
    if (badge) {
      badge.textContent = count > 99 ? '99+' : String(count);
    } else if (count > 0) {
      // Crear badge si no existe
      badge = document.createElement('span');
      badge.className = 'pya-like-count-badge';
      badge.textContent = count > 99 ? '99+' : String(count);
      btnEl.appendChild(badge);
    }
  }

  // ===== MANEJAR CLICK CON ANIMACIONES =====
  async function handleClick(businessId, btnEl) {
    const icon = btnEl.querySelector('i');
    let token = getToken();

    if (!token) {
      // Intentar recuperar desde cookie HttpOnly
      token = await recoverTokenFromCookie();
    }

    if (!token) {
      if (global.showToast) global.showToast('Inicia sesión para dar me gusta', 'warning');
      setTimeout(() => { window.location.href = '/login.html'; }, 1500);
      return;
    }

    const wasLiked = btnEl.dataset.liked === 'true';

    // Optimistic UI update inmediato
    if (wasLiked) {
      // → UNLIKE
      icon.className = 'far fa-heart';
      icon.style.color = '#9ca3af';
      icon.style.textShadow = 'none';
      btnEl.dataset.liked = 'false';
      btnEl.setAttribute('aria-label', 'Dar me gusta');
      btnEl.title = 'Me gusta';
      btnEl.style.animation = 'pya-heart-unlike 0.3s ease';
      setTimeout(() => { btnEl.style.animation = ''; }, 350);
    } else {
      // → LIKE con animación espectacular
      icon.className = 'fas fa-heart';
      icon.style.color = '#ef4444';
      icon.style.textShadow = '0 0 6px rgba(239,68,68,0.4)';
      btnEl.dataset.liked = 'true';
      btnEl.setAttribute('aria-label', 'Quitar me gusta');
      btnEl.title = 'Quitar me gusta';
      btnEl.style.animation = 'pya-heart-beat 0.5s ease';
      setTimeout(() => { btnEl.style.animation = ''; }, 550);
      burstParticles(btnEl);
      addRipple(btnEl);
    }

    // Llamada API (después del UI optimista)
    try {
      const resp = await fetch(`${API_BASE}/likes/${businessId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await resp.json();

      if (data.status === 'success') {
        const nowLiked = data.data.liked;
        // Sincronizar con respuesta del servidor
        if (nowLiked !== (btnEl.dataset.liked === 'true')) {
          icon.className = nowLiked ? 'fas fa-heart' : 'far fa-heart';
          icon.style.color = nowLiked ? '#ef4444' : '#9ca3af';
          icon.style.textShadow = nowLiked ? '0 0 6px rgba(239,68,68,0.4)' : 'none';
          btnEl.dataset.liked = nowLiked ? 'true' : 'false';
          btnEl.setAttribute('aria-label', nowLiked ? 'Quitar me gusta' : 'Dar me gusta');
          btnEl.title = nowLiked ? 'Quitar me gusta' : 'Me gusta';
        }
        // Actualizar contador desde la API
        if (typeof data.data.likes_count === 'number') {
          updateLikesCountBadge(btnEl, data.data.likes_count);
        }
        // Actualizar caché
        if (likesCache) {
          if (nowLiked) likesCache.add(businessId);
          else likesCache.delete(businessId);
          try { sessionStorage.setItem(LIKES_CACHE_KEY, JSON.stringify(Array.from(likesCache))); } catch {}
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revertir UI optimista en caso de error
      const revertTo = wasLiked;
      icon.className = revertTo ? 'fas fa-heart' : 'far fa-heart';
      icon.style.color = revertTo ? '#ef4444' : '#9ca3af';
      icon.style.textShadow = revertTo ? '0 0 6px rgba(239,68,68,0.4)' : 'none';
      btnEl.dataset.liked = revertTo ? 'true' : 'false';
      btnEl.setAttribute('aria-label', revertTo ? 'Quitar me gusta' : 'Dar me gusta');
      btnEl.title = revertTo ? 'Quitar me gusta' : 'Me gusta';
      if (global.showToast) global.showToast('Error al dar me gusta', 'error');
    }
  }

  // ===== FUNCIÓN UNIVERSAL: attachLikeButton =====
  // Se llama desde CUALQUIER lugar que renderice tarjetas
  // Uso: LikesUI.attachLikeButton(containerElement, businessId)
  // Agrega el botón al container y maneja todo
  function attachLikeButton(container, businessId) {
    if (!container || !businessId) return;
    // Evitar duplicados
    if (container.querySelector('.pya-like-btn')) return;
    injectAnimations();
    const btn = createLikeButton(businessId);
    container.appendChild(btn);
    return btn;
  }

  // ===== FORMA CORTA: attach a un elemento específico (posición) =====
  // Crea un wrapper con el botón y lo inserta en la posición deseada
  function createLikesWrapper(businessId) {
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'display:inline-flex; align-items:center; gap:0.35rem;';
    const btn = createLikeButton(businessId);
    wrapper.appendChild(btn);
    return wrapper;
  }

  // ===== INICIALIZAR =====
  async function init() {
    injectAnimations();
    await loadLikedIds();
    return likesCache;
  }

  // ===== API PÚBLICA =====
  global.LikesUI = {
    init,
    isLiked,
    createLikeButton,
    attachLikeButton,
    createLikesWrapper,
    loadLikedIds
  };

  // Auto-inicializar al cargar
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    init().catch(() => {});
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      init().catch(() => {});
    });
  }

})(typeof window !== 'undefined' ? window : this);
